import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { fetchPickListDetailsAutoComplete } from '../../../store/apis/autocompleteService';

function debouncePromise(fn, delay) {
  let timeoutId;
  let lastReject;
  return (...args) =>
    new Promise((resolve, reject) => {
      if (timeoutId) clearTimeout(timeoutId);
      if (lastReject) lastReject();
      lastReject = reject;
      timeoutId = setTimeout(() => {
        fn(...args)
          .then(resolve)
          .catch(reject);
      }, delay);
    });
}

function TravelAsyncAutocompleteSelect({
  placeholder,
  autoCompleteFn,
  mappedFn,
  onChange,
  displayOption = 'both',
  value,
}) {
  const [currencyValue, setCurrencyValue] = useState(null);
  // Style for the select component
  const selectStyles = () => ({
    control: (base, state) => ({
      ...base,
      border: state.isFocused ? '1px solid #ced4da' : '1px solid #929398',
      boxShadow: state.isFocused
        ? '0 0 0 0.15rem rgba(206, 212, 218, 0.6)'
        : '0',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      transition:
        'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      '&:hover': {
        borderColor: '#ced4da',
      },
    }),
    valueContainer: base => ({
      ...base,
      paddingRight: '0',
      cursor: 'text',
    }),
    indicatorsContainer: base => ({
      ...base,
      padding: '0',
    }),
    dropdownIndicator: base => ({
      ...base,
    }),
    input: base => ({
      ...base,
      paddingRight: '0',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999,
    }),
    menu: base => ({
      ...base,
    }),
    menuList: base => ({
      ...base,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  });

  // Render Option Label
  const formatOptionLabel = (option, { context }) => {
    if (context === 'value') {
      return <span>{option.value}</span>;
    }

    if (displayOption === 'value') {
      return option.value;
    }

    if (displayOption === 'both') {
      return (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ width: '70%' }}>
            <span>{option.label}</span>
          </div>
          <span
            style={{
              width: '1px',
              backgroundColor: option.color || '#ccc',
              alignSelf: 'stretch',
            }}
          />
          <span style={{ opacity: 0.6, marginLeft: '10px' }}>
            {option.value}
          </span>
        </div>
      );
    }

    return option.label;
  };

  // Debounced loadOptions
  const debouncedLoadOptions = useRef(
    debouncePromise(async inputValue => {
      if (!inputValue) return [];
      const data = await autoCompleteFn(inputValue);
      if (mappedFn) {
        const mappedData = mappedFn(data);
        return mappedData;
      }
      return data;
    }, 600),
  ).current;

  const { mutate: getCurrencyDetails } = useMutation({
    mutationFn: payload => fetchPickListDetailsAutoComplete(payload),
    onSuccess: data => {
      const matchedCurrency = data.find(
        item => item.Code.trim() === value.trim(),
      );
      if (matchedCurrency) {
        setCurrencyValue({
          label: matchedCurrency?.Name,
          value: matchedCurrency?.Code,
        });
      } else {
        setCurrencyValue(null);
      }
    },
    onError: error => {
      toast.error('Failed to fetch currency details.', error, {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (value && value?.trim()?.length > 2) {
      getCurrencyDetails({
        type: 'HR_CURRENCY',
        prefixText: value.trim(),
      });
    }
  }, [value, getCurrencyDetails]);

  return (
    <div>
      <AsyncSelect
        cacheOptions
        loadOptions={debouncedLoadOptions}
        defaultOptions
        onChange={onChange}
        placeholder={placeholder}
        styles={selectStyles()}
        isSearchable
        menuPortalTarget={document.body}
        formatOptionLabel={formatOptionLabel}
        value={currencyValue}
        noOptionsMessage={() => null}
        components={{
          LoadingIndicator: () => null,
        }}
      />
    </div>
  );
}

export default TravelAsyncAutocompleteSelect;
